/**
 * ops 依頼の SLO / 件数系の集計（純関数）の単一 source。ダッシュボードの SLO タブ（サマリーカード・サービス別・
 * SLA 未達一覧）、ボリューム推移チャート、内訳タブ（メンバー別）が同じ定義を参照し、画面ごとに「期限超過」や
 * 「工数」の数え方がずれないようにする。1 件の判定は `computeOpsSloOutcome`（sla）、処理日数は
 * `opsProcessingBusinessDays`（sla）に委譲し、ここは「依頼の集合をどう数えるか」だけを持つ。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼の集合から 未対応件数・SLA 未達件数・SLO 達成率・期限超過率・SLO 目標達成の判定・バグ転換率・総件数・総工数・平均処理営業日数・担当者別内訳 を導く共有集計。SLO タブ・推移チャート・内訳タブ・customer-success MCP が同じ定義を使う
 * @graph-connects computeOpsSloOutcome [calls] 1 件の SLA 対象・目標達成の判定
 * @graph-connects opsProcessingBusinessDays [calls] 1 件の起票→完了の営業日数
 */
import { OPS_SLO_TARGET_PERCENT, computeOpsSloOutcome, opsProcessingBusinessDays, } from './sla.js';
/** 判定結果が「SLA 対象で目標超過」か。期限超過の定義そのもので、1 件判定と集合の集計の両方がここを通る。 @graph-connects none */
function isBreachedOutcome(outcome) {
    return outcome.eligible && !outcome.metTarget;
}
/**
 * 依頼が SLA 対象で、目標営業日を超えたか（解決済み=解決までに超過、未解決=現時点で超過）。
 * 一覧の絞り込み用の 1 件版（件数・率は tallyOpsSlo が同じ isBreachedOutcome で数える）。
 * @graph-connects computeOpsSloOutcome [calls] SLA 対象・目標達成の判定
 * @graph-connects isBreachedOutcome [calls] 期限超過の定義
 */
export function isOpsSloBreached(row, now) {
    return isBreachedOutcome(computeOpsSloOutcome(row, now));
}
/**
 * 目標営業日をどれだけ超えたか（解決済み=解決までの、未解決=現時点までの経過 − 目標）。目標内・SLA 対象外は 0。
 * @graph-connects computeOpsSloOutcome [calls] 経過営業日と目標営業日
 */
export function opsSloOverDays(row, now) {
    const outcome = computeOpsSloOutcome(row, now);
    if (!outcome.eligible || outcome.targetDays === null)
        return 0;
    return Math.max(0, outcome.elapsedDays - outcome.targetDays);
}
/**
 * SLO 達成率（%）が目標ライン（OPS_SLO_TARGET_PERCENT）以上か。率が出ない（対象 0 件）なら null。
 * SLO タブの色分けと内訳タブの期限超過の強調が同じ基準を使うための単一の判定（画面側で閾値を比べない）。
 * @graph-connects none
 */
export function isOpsSloRateOnTarget(rate) {
    return rate === null ? null : rate >= OPS_SLO_TARGET_PERCENT;
}
/**
 * 割合（%、整数に四捨五入）。分母 0 は null（「0%」と「対象なし」を混ぜない）。ops の率（SLO 達成率・
 * 期限超過率・バグ転換率・差戻し率）はすべてこの丸め方にそろえる。
 * @graph-connects none
 */
export function opsPercent(part, total) {
    return total === 0 ? null : Math.round((part / total) * 100);
}
/**
 * 平均（小数 1 桁に四捨五入）。値が無ければ null。ops の平均（処理日数・差戻しからの再開時間）はこの丸め方にそろえる。
 * @graph-connects none
 */
export function opsMean1(values) {
    if (values.length === 0)
        return null;
    return Math.round((values.reduce((sum, v) => sum + v, 0) / values.length) * 10) / 10;
}
/** 総件数（期間 / 絞り込み内の全依頼数）。 @graph-connects none */
export function opsTotalCount(rows) {
    return rows.length;
}
/** 総工数（point の合計。未確定 point は 0 として扱う）。 @graph-connects none */
export function opsTotalPoint(rows) {
    return rows.reduce((sum, r) => sum + (r.point ?? 0), 0);
}
/** 未クローズ（未対応）件数。 @graph-connects none */
export function opsCountOpen(rows) {
    return rows.filter((r) => r.state !== 'closed').length;
}
/**
 * 依頼の集合を 1 回走査して SLA 対象件数と超過件数を数える（1 件につき判定は 1 回）。SLA 未達件数・
 * SLO 達成率・期限超過率はすべてこの結果から導く。
 * @graph-connects computeOpsSloOutcome [calls] 1 件の SLA 対象・目標達成の判定
 * @graph-connects isBreachedOutcome [calls] 期限超過の定義
 */
function tallyOpsSlo(rows, now) {
    let eligible = 0;
    let breached = 0;
    for (const row of rows) {
        const outcome = computeOpsSloOutcome(row, now);
        if (outcome.eligible)
            eligible += 1;
        if (isBreachedOutcome(outcome))
            breached += 1;
    }
    return { eligible, breached };
}
/**
 * SLA 目標を超過した件数（解決済み=解決遅延、未解決=現時点で超過）。
 * @graph-connects tallyOpsSlo [calls] SLA 対象・超過の件数
 */
export function opsCountBreaching(rows, now) {
    return tallyOpsSlo(rows, now).breached;
}
/**
 * SLO 達成率（%）。SLA 対象依頼のうち目標内に解決（未解決は現時点でまだ目標内）の割合。解決日ベースで判定する
 * ため、過去期間（解決済みが大半）でも 0% に潰れない。対象が 0 件なら null。
 * @graph-connects tallyOpsSlo [calls] SLA 対象・超過の件数
 */
export function opsSloRate(rows, now) {
    const { eligible, breached } = tallyOpsSlo(rows, now);
    return opsPercent(eligible - breached, eligible);
}
/**
 * 期限超過率（%）。SLA 対象依頼のうち目標を超えた割合（SLO 達成率と同じ分母を達成 / 超過で分ける）。
 * 対象が 0 件なら null。
 * @graph-connects tallyOpsSlo [calls] SLA 対象・超過の件数
 */
export function opsOverdueRate(rows, now) {
    const { eligible, breached } = tallyOpsSlo(rows, now);
    return opsPercent(breached, eligible);
}
/** バグ転換率（%）。クローズ済みのうち バグチケット化（classification=bug）された割合。0 件なら null。 @graph-connects none */
export function opsBugRate(rows) {
    const closed = rows.filter((r) => r.state === 'closed');
    return opsPercent(closed.filter((r) => r.classification === 'bug').length, closed.length);
}
/**
 * 平均処理日数（完了済みの起票→完了の営業日、小数 1 桁）。完了が 0 件なら null。
 * @graph-connects opsProcessingBusinessDays [calls] 1 件の起票→完了の営業日数
 * @graph-connects opsMean1 [calls] 平均の丸め
 */
export function opsAvgProcessingDays(rows) {
    return opsMean1(rows
        .map((r) => opsProcessingBusinessDays(r.submittedAt, r.completedAt))
        .filter((d) => d !== null));
}
/**
 * 依頼の集合を担当者別に集計する。担当は依頼の現在の担当者で、途中で替わった依頼は最後の担当者に計上する。
 * 並びは件数の多い順（同数は名前順）、未割当は常に末尾。各指標は上の集計関数をそのまま使う。
 * @graph-connects opsTotalCount [calls] 件数
 * @graph-connects opsTotalPoint [calls] 工数
 * @graph-connects opsOverdueRate [calls] 期限超過率
 * @graph-connects opsSloRate [calls] SLO 達成率（目標判定用）
 * @graph-connects isOpsSloRateOnTarget [calls] 目標ラインとの比較
 * @graph-connects opsAvgProcessingDays [calls] 平均処理日数
 */
export function aggregateOpsByMember(rows, now) {
    const groups = new Map();
    for (const row of rows) {
        groups.set(row.assigneeName, [...(groups.get(row.assigneeName) ?? []), row]);
    }
    return [...groups.entries()]
        .map(([member, items]) => ({
        member,
        count: opsTotalCount(items),
        points: opsTotalPoint(items),
        overdueRate: opsOverdueRate(items, now),
        meetsSloTarget: isOpsSloRateOnTarget(opsSloRate(items, now)),
        avgProcessingDays: opsAvgProcessingDays(items),
    }))
        .sort((a, b) => {
        if (a.member === null)
            return 1;
        if (b.member === null)
            return -1;
        return b.count - a.count || a.member.localeCompare(b.member);
    });
}
//# sourceMappingURL=slo-aggregate.js.map